// How a submitted search prompt is shown when something other than the search
// itself has to name it — the sidebar's draft ongoing row (#32) and the
// Timeline's "Search opened" item (#30).
//
// Both want the same thing: the prompt as asked, on one line, short enough for
// a narrow column. They differ only in how much room they have, so the cap is a
// parameter and the rule lives in one place.

/** Sidebar rows are narrower than the timeline panel. */
export const SIDEBAR_MAX = 60
export const TIMELINE_MAX = 80

/**
 * The prompt as a single short line.
 *
 * A pasted paragraph must not push a row out of shape, and only the first line
 * carries the intent — the rest is detail the surface has no room for.
 *
 * @param {string｜null｜undefined} question
 * @param {number} [max]
 * @returns {string} the label, or '' when there is nothing to show.
 */
export function searchLabel(question, max = TIMELINE_MAX) {
  return (question || '').trim().split('\n')[0].slice(0, max)
}

/**
 * The prompt as a SENTENCE: its first letter capitalised (Pouria, 2026-08-17 — "the first letter of
 * the research it should be in upercase in input and in the middle when we wrote").
 *
 * Only the FIRST letter, and only where capitalising it means something:
 *  - leading whitespace is kept and skipped over, so a prompt typed after a stray space still reads
 *    as a sentence;
 *  - a prompt that opens with a URL is left exactly as typed — "Https://…" is not a nicety, it is a
 *    broken link in a snippet someone will paste back out;
 *  - a number, a quote or an emoji has no upper case, so nothing happens;
 *  - everything after that first letter is the user's, so a brand written in lower case mid-sentence
 *    stays as they wrote it.
 *
 * Deliberately NOT `text-transform: capitalize`, which capitalises every word, and not
 * `toLocaleUpperCase`, so the result never depends on the browser's locale (Turkish dotless i).
 *
 * @param {string｜null｜undefined} text
 * @returns {string} the text with its opening letter in upper case ('' for nothing)
 */
export function capitalizeFirst(text) {
  const t = text == null ? '' : String(text)
  const at = t.search(/\S/)
  if (at < 0) return t
  if (/^(?:[a-z][\w+.-]*:\/\/|www\.)/i.test(t.slice(at))) return t // a link, left alone
  const first = t.charAt(at)
  const upper = first.toUpperCase()
  if (upper === first) return t // no upper case to give it (a digit, a quote, an emoji)
  return t.slice(0, at) + upper + t.slice(at + 1)
}

/**
 * What a research turn is CALLED on screen.
 *
 * Normally a turn is shown by the question that was asked. Some asks are built for the model rather
 * than for reading — the one-pack comparison sends a paragraph of table instructions (Pouria,
 * 2026-08-17: "compare to entenrt should get a title for compare") — and those carry a short
 * `title` written for the human. Everything that NAMES a turn reads it through here: the middle's
 * question chip, the home answer, the rail's "Search opened", the sidebar's draft row, the search
 * briefing's own heading, and the pack name a save proposes.
 */
export const turnLabel = (turn) => (turn && (turn.title || turn.question)) || ''
